/**
 * Inventory Repository
 *
 * Handles all inventory-related data access with Zod schema validation.
 * Part of the Service/Repository pattern refactor (Chunk 2).
 *
 * @module services/repositories/InventoryRepository
 */

import { z } from 'zod';
import { supabase } from '../supabase';
import { isSchemasCacheError } from '../supabaseErrors';
import { auditLogger } from '../auditLogger';
import type {
  InventoryInsight,
  InventoryQualityMetrics,
  StoreFinancialSummary,
  ProductFinancials,
  CategoryFinancials,
  TopShrinkageItem,
  AvailabilityDiscrepancyItem,
  PhantomItem,
  CashierRisk,
  MatrixItem
} from '../../types';

// ============================================
// ZOD SCHEMAS
// ============================================

/**
 * Helper for PostgreSQL NUMERIC/DECIMAL fields that return as strings
 */
const numericString = z.union([z.number(), z.string()]).nullable().transform(v =>
  v === null || v === undefined ? null : Number(v)
);

/**
 * Schema for raw inventory record from database
 */
const InventoryRecordSchema = z.object({
  id: z.string().uuid(),
  product_id: z.string().uuid(),
  store_id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  system_qty: z.number().nullable().default(0),
  physical_qty: z.number().nullable().default(0),
  last_audit_date: z.string().nullable(),
  location: z.string().nullable(),
  // LP fields (v5.32.0)
  qty_available_to_sell: z.number().nullable().optional(),
  qty_in_backroom: z.number().nullable().optional(),
  qty_on_shelf: z.number().nullable().optional(),
  qty_in_transit: z.number().nullable().optional(),
  qty_damaged: z.number().nullable().optional(),
  qty_quarantine: z.number().nullable().optional(),
  last_count_date: z.string().nullable().optional(),
  unit_cost_current: numericString.optional(),
});

/**
 * Schema for product joined data
 * Note: PostgreSQL NUMERIC types are returned as strings, so we coerce them
 * All fields optional except id/sku/name since queries may not select all columns
 */
const ProductJoinSchema = z.object({
  id: z.string().uuid(),
  sku: z.string(),
  name: z.string(),
  category: z.string().nullable().optional(),
  unit_cost: numericString.optional(),
  unit_price: numericString.optional(),
  // LP fields (v5.32.0)
  gtin: z.string().nullable().optional(),
  ean: z.string().nullable().optional(),
  catch_weight: z.boolean().nullable().optional(),
  nominal_weight_kg: numericString.optional(),
  pack_size: z.number().nullable().optional(),
  case_size: z.number().nullable().optional(),
  last_purchase_cost: numericString.optional(),
});

/**
 * Schema for inventory with product join
 */
const InventoryWithProductSchema = z.object({
  product_id: z.string().uuid(),
  system_qty: z.number().nullable(),
  physical_qty: z.number().nullable(),
  last_audit_date: z.string().nullish(), // nullish = nullable + optional (accepts string | null | undefined)
  products: ProductJoinSchema.nullable(),
});

/**
 * Schema for inventory quality metrics from database
 * Note: All metrics are DECIMAL/NUMERIC and return as strings from PostgreSQL
 */
const InventoryQualityMetricsDbSchema = z.object({
  id: z.string().uuid().optional(),
  store_id: z.string().uuid(),
  ira: numericString,
  phantom_rate: numericString,
  gmroi: numericString,
  doh: numericString,
  sell_through: numericString,
  planogram_compliance: numericString,
  // Optional so rows from older deployments without the column still validate
  shrinkage_rate: numericString.optional(),
  calculated_at: z.string().nullable(),
});

/**
 * Inventory quality metrics as returned by getInventoryQuality.
 *
 * shrinkageRate = inventory_quality_metrics.shrinkage_rate, written by the
 * quality-metrics trigger as (shrinkage value at cost / on-hand inventory cost) * 100.
 * Percent units (2.5 = 2.5%). null when no metrics row exists or the column is null.
 * NOT the "Merma %" (shrinkage / sales) shown on the layer KPI cards — that ratio comes from
 * daily_store_stats via store_comparison_summary / view_dashboard_kpis.
 *
 * TODO: fold shrinkageRate into InventoryQualityMetrics in types.ts.
 */
export type InventoryQualityMetricsResult = InventoryQualityMetrics & {
  shrinkageRate: number | null;
};

/** Null-preserving numeric read — Number(null) would silently become 0 */
const toNullableNumber = (v: number | null | undefined): number | null =>
  v === null || v === undefined || !Number.isFinite(Number(v)) ? null : Number(v);

/**
 * Schema for phantom inventory item from database
 */
const PhantomInventoryDbSchema = z.object({
  id: z.string().uuid(),
  product_id: z.string().uuid(),
  system_stock: z.number().nullable(),
  physical_stock: z.number().nullable(),
  variance: z.number().nullable(),
  variance_value: numericString,
  products: z.object({
    sku: z.string().nullable(),
    name: z.string().nullable(),
    category: z.string().nullable(),
    unit_price: numericString,
  }).nullable(),
});

/**
 * Schema for cashier risk profile from database
 */
const CashierRiskProfileDbSchema = z.object({
  id: z.string().uuid().optional(),
  user_id: z.string().uuid().optional().nullable(),
  cashier_name: z.string().nullable(),
  risk_score: z.number().nullable(),
  void_rate: numericString,
  return_rate: numericString,
  transaction_count: z.number().nullable(),
});

/**
 * Schema for transaction from database
 */
const TransactionDbSchema = z.object({
  product_id: z.string().uuid(),
  type: z.string().nullable(),
  quantity: z.number().nullable(),
  total_amount: numericString,
});

/**
 * Output schema for TopShrinkageItem
 */
const TopShrinkageItemSchema = z.object({
  product: z.string(),
  loss: z.number(),
  sku: z.string(),
  trend: z.enum(['increasing', 'decreasing', 'stable']),
  recentAlerts: z.number(),
});

/**
 * Output schema for AvailabilityDiscrepancyItem
 */
const AvailabilityDiscrepancyItemSchema = z.object({
  product: z.string(),
  system: z.number(),
  floor: z.number(),
  sku: z.string(),
  lastCount: z.string(),
});

/**
 * Output schema for InventoryInsight
 */
const InventoryInsightSchema = z.object({
  topShrinkage: z.array(TopShrinkageItemSchema),
  availabilityDiscrepancies: z.array(AvailabilityDiscrepancyItemSchema),
});

/**
 * Output schema for ProductFinancials
 */
const ProductFinancialsSchema = z.object({
  sku: z.string(),
  name: z.string(),
  category: z.string(),
  cost: z.number(),
  price: z.number(),
  margin: z.number(),
  marginPercent: z.number(),
  velocity: z.number(),
  accuracyScore: z.number(),
  lostProfitOpportunity: z.number(),
  trend: z.enum(['up', 'down', 'stable']),
});

/**
 * Output schema for CategoryFinancials
 */
const CategoryFinancialsSchema = z.object({
  name: z.string(),
  totalLostProfit: z.number(),
  totalValue: z.number(),
  itemCount: z.number(),
});

/**
 * Output schema for StoreFinancialSummary
 */
const StoreFinancialSummarySchema = z.object({
  totalLostProfit: z.number(),
  lastPeriodLostProfit: z.number(),
  topPerformers: z.array(ProductFinancialsSchema),
  profitKillers: z.array(ProductFinancialsSchema),
  categoryBreakdown: z.array(CategoryFinancialsSchema),
});

// ============================================
// REPOSITORY IMPLEMENTATION
// ============================================

/**
 * InventoryRepository handles all inventory-related data access operations.
 * All return data is validated with Zod schemas for runtime type safety.
 */
export const InventoryRepository = {
  /**
   * Get inventory insights for a store including top shrinkage and availability discrepancies.
   * SECURITY: tenantId is required to prevent cross-tenant data leakage.
   * @param tenantId - Required tenant UUID for security filtering
   * @param storeId - The store UUID
   * @returns InventoryInsight with validated data
   */
  async getInventoryInsights(tenantId: string, storeId: string): Promise<InventoryInsight> {
    // SECURITY: Require tenantId to prevent data leakage
    if (!tenantId) {
      console.error('[InventoryRepository.getInventoryInsights] tenantId is required for security');
      return { topShrinkage: [], availabilityDiscrepancies: [] };
    }

    auditLogger.log({ tenantId, action: 'view', entityType: 'inventory', details: { query: 'insights', storeId } });

    try {
      const { data: inventory, error } = await supabase
        .from('inventory')
        .select(`
          product_id,
          system_qty,
          physical_qty,
          last_audit_date,
          products (
            id,
            sku,
            name,
            unit_price
          )
        `)
        .eq('tenant_id', tenantId)  // SECURITY: Always filter by tenant
        .eq('store_id', storeId);

      if (error) {
        if (isSchemasCacheError(error)) {
          console.debug('[InventoryRepository] Inventory table not available');
        } else {
          console.error('[InventoryRepository] Failed to fetch inventory insights:', error);
        }
        return { topShrinkage: [], availabilityDiscrepancies: [] };
      }

      if (!inventory || inventory.length === 0) {
        console.warn('[InventoryRepository] No inventory data returned for store:', storeId);
        return { topShrinkage: [], availabilityDiscrepancies: [] };
      }

      console.log('[InventoryRepository] Raw inventory count:', inventory.length, 'Sample:', inventory[0]);

      // Parse and validate raw data
      const validatedInventory = inventory
        .map(inv => {
          try {
            return InventoryWithProductSchema.parse(inv);
          } catch (e) {
            console.warn('[InventoryRepository] Zod validation failed for item:', inv, 'Error:', e);
            return null;
          }
        })
        .filter((inv): inv is z.infer<typeof InventoryWithProductSchema> => inv !== null && inv.products !== null);

      console.log('[InventoryRepository] Validated inventory count:', validatedInventory.length);

      // Calculate shrinkage and discrepancies
      // Only include items where BOTH system_qty and physical_qty are present
      // Items without physical_qty have not been counted yet (no variance to report)
      const items = validatedInventory
        .filter(inv => inv.physical_qty !== null && inv.physical_qty !== undefined)
        .map(inv => {
          const systemQty = inv.system_qty || 0;
          const physicalQty = inv.physical_qty || 0;
          const variance = systemQty - physicalQty;
          const price = Number(inv.products?.unit_price) || 0;
          return {
            product: inv.products?.name || 'Desconocido',
            sku: inv.products?.sku || '',
            system: systemQty,
            floor: physicalQty,
            variance,
            loss: variance > 0 ? variance * price : 0,
            lastAudit: inv.last_audit_date
          };
        });

      // Top shrinkage: items with highest loss
      const topShrinkage: TopShrinkageItem[] = items
        .filter(i => i.loss > 0)
        .sort((a, b) => b.loss - a.loss)
        .slice(0, 5)
        .map(i => ({
          product: i.product,
          loss: i.loss,
          sku: i.sku,
          trend: 'stable' as const,
          recentAlerts: 0
        }));

      // Availability discrepancies: items with any variance
      const availabilityDiscrepancies: AvailabilityDiscrepancyItem[] = items
        .filter(i => Math.abs(i.variance) > 0)
        .sort((a, b) => Math.abs(b.variance) - Math.abs(a.variance))
        .slice(0, 5)
        .map(i => ({
          product: i.product,
          system: i.system,
          floor: i.floor,
          sku: i.sku,
          lastCount: i.lastAudit ? new Date(i.lastAudit).toLocaleDateString('es-CL') : 'Nunca'
        }));

      // Validate output
      const result = InventoryInsightSchema.parse({ topShrinkage, availabilityDiscrepancies });
      return result;
    } catch (err) {
      console.error('[InventoryRepository] Error fetching inventory insights:', err);
      return { topShrinkage: [], availabilityDiscrepancies: [] };
    }
  },

  /**
   * Get inventory quality metrics for a store.
   * SECURITY: tenantId is required to prevent cross-tenant data leakage.
   * @param tenantId - Required tenant UUID for security filtering
   * @param storeId - The store UUID
   * @returns InventoryQualityMetricsResult with validated data
   */
  async getInventoryQuality(tenantId: string, storeId: string): Promise<InventoryQualityMetricsResult> {
    // SECURITY: Require tenantId to prevent data leakage
    if (!tenantId) {
      console.error('[InventoryRepository.getInventoryQuality] tenantId is required for security');
      return this.getEmptyQualityMetrics();
    }

    auditLogger.log({ tenantId, action: 'view', entityType: 'inventory', details: { query: 'quality', storeId } });

    try {
      // Fetch quality metrics from inventory_quality_metrics table
      // SECURITY: Filter by tenant_id for defense-in-depth (RLS is backup)
      const { data: metrics, error: metricsError } = await supabase
        .from('inventory_quality_metrics')
        .select('*')
        .eq('tenant_id', tenantId)  // SECURITY: Always filter by tenant
        .eq('store_id', storeId)
        .order('calculated_at', { ascending: false })
        .limit(1)
        .single();

      // Get phantom inventory items
      // SECURITY: Filter by tenant_id for defense-in-depth
      const { data: phantomData } = await supabase
        .from('phantom_inventory_history')
        .select(`
          id,
          product_id,
          system_stock,
          physical_stock,
          variance,
          variance_value,
          products (
            sku,
            name,
            category,
            unit_price
          )
        `)
        .eq('tenant_id', tenantId)  // SECURITY: Always filter by tenant
        .eq('store_id', storeId)
        .gt('variance', 0)
        .order('variance_value', { ascending: false })
        .limit(10);

      // Get cashier risk profiles
      // SECURITY: Filter by tenant_id for defense-in-depth
      const { data: cashierData } = await supabase
        .from('cashier_risk_profiles')
        .select('*')
        .eq('tenant_id', tenantId)  // SECURITY: Always filter by tenant
        .eq('store_id', storeId)
        .order('risk_score', { ascending: false })
        .limit(10);

      // Validate and transform phantom items with history
      const phantomItems: PhantomItem[] = await Promise.all(
        (phantomData || []).map(async (p: unknown) => {
          try {
            const validated = PhantomInventoryDbSchema.parse(p);

            // Fetch 14-day history for this product from transactions (NO MOCK DATA)
            const fourteenDaysAgo = new Date();
            fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);
            const productId = validated.product_id;

            // Query real transaction data for this specific product
            // SECURITY: Filter by tenant_id for defense-in-depth
            const { data: productTransactions } = await supabase
              .from('transactions')
              .select('created_at, quantity, type')
              .eq('tenant_id', tenantId)  // SECURITY: Always filter by tenant
              .eq('store_id', storeId)
              .eq('product_id', productId)
              .gte('created_at', fourteenDaysAgo.toISOString())
              .order('created_at', { ascending: true });

            // Group transactions by day and sum sales
            const dailySales = new Map<string, number>();
            for (let i = 13; i >= 0; i--) {
              const date = new Date();
              date.setDate(date.getDate() - i);
              const dateKey = date.toISOString().split('T')[0];
              dailySales.set(dateKey, 0);
            }

            (productTransactions || []).forEach((tx: { created_at: string; quantity: number; type: string | null }) => {
              if (!tx.created_at) return;
              const dateKey = tx.created_at.split('T')[0];
              const existing = dailySales.get(dateKey);
              const txType = tx.type?.toLowerCase();
              // Count sales and returns as negative sales
              if (txType === 'sale' || txType === 'venta') {
                dailySales.set(dateKey, (existing || 0) + Math.abs(tx.quantity));
              }
            });

            // Build history array for chart (NO MOCK DATA - uses real transaction sums)
            const history: Array<{ date: string; stock: number; sales: number }> = [];
            dailySales.forEach((sales, dateKey) => {
              history.push({
                date: new Date(dateKey).toLocaleDateString('es-CL', { day: '2-digit', month: 'short' }),
                stock: validated.system_stock || 0, // Current stock (we don't have historical snapshots)
                sales: sales // Real sales from transactions
              });
            });

            // Get days since last sale from phantom table
            const rawPhantom = p as Record<string, unknown>;
            const daysNoSale = (rawPhantom.days_since_last_sale as number) || 14;
            const avgDaily = (rawPhantom.avg_daily_sales as number) || 0;
            const confidence = (rawPhantom.confidence as number) || 70;

            return {
              sku: validated.products?.sku || 'Desconocido',
              name: validated.products?.name || 'Producto desconocido',
              systemStock: validated.system_stock || 0,
              daysSinceLastSale: daysNoSale,
              avgDailySales: avgDaily,
              confidence: confidence,
              history
            };
          } catch {
            return null;
          }
        })
      ).then(items => items.filter((item): item is PhantomItem => item !== null));

      // Validate and transform cashier risks with recent transactions
      const cashierRisks: CashierRisk[] = (cashierData || []).map((c: unknown) => {
        try {
          const validated = CashierRiskProfileDbSchema.parse(c);
          const raw = c as Record<string, unknown>;

          // Parse recent_transactions JSONB array
          const recentTxns = Array.isArray(raw.recent_transactions)
            ? raw.recent_transactions
            : (typeof raw.recent_transactions === 'string'
              ? JSON.parse(raw.recent_transactions)
              : []);

          return {
            id: validated.user_id || validated.id || crypto.randomUUID(),
            name: validated.cashier_name || 'Cajero desconocido',
            riskScore: Number(validated.risk_score) || 0,
            voidRate: Number(validated.void_rate) || 0,
            returnRate: Number(validated.return_rate) || 0,
            recentTransactions: recentTxns.map((tx: Record<string, unknown>) => ({
              time: String(tx.time || ''),
              type: String(tx.type || 'UNKNOWN'),
              item: String(tx.item || ''),
              amount: Number(tx.amount || 0),
              details: String(tx.details || '')
            }))
          };
        } catch {
          return null;
        }
      }).filter((item): item is CashierRisk => item !== null);

      // Create matrix data from inventory with variance
      // SECURITY: Filter by tenant_id for defense-in-depth
      const { data: inventoryData } = await supabase
        .from('inventory')
        .select(`
          product_id,
          system_qty,
          physical_qty,
          products (
            sku,
            name,
            category,
            unit_cost,
            unit_price
          )
        `)
        .eq('tenant_id', tenantId)  // SECURITY: Always filter by tenant
        .eq('store_id', storeId)
        .limit(50);

      // Get sales data to calculate per-product metrics
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      // SECURITY: Filter by tenant_id for defense-in-depth
      const { data: salesData } = await supabase
        .from('transactions')
        .select('product_id, quantity, total_amount, products!inner(unit_cost)')
        .eq('tenant_id', tenantId)  // SECURITY: Always filter by tenant
        .eq('store_id', storeId)
        .eq('type', 'SALE')
        .gte('created_at', thirtyDaysAgo.toISOString());

      // Build a map of product sales for DOH/GMROI calculation
      const productSales: Record<string, {
        totalQty: number;
        totalRevenue: number;
        totalCOGS: number;
        avgDailySales: number;
      }> = {};

      for (const sale of (salesData || [])) {
        const productId = sale.product_id;
        if (!productSales[productId]) {
          productSales[productId] = { totalQty: 0, totalRevenue: 0, totalCOGS: 0, avgDailySales: 0 };
        }
        const qty = Math.abs(sale.quantity || 0);
        productSales[productId].totalQty += qty;
        productSales[productId].totalRevenue += Number(sale.total_amount) || 0;
        const unitCost = (sale.products as { unit_cost: number | null } | null)?.unit_cost || 0;
        productSales[productId].totalCOGS += qty * unitCost;
      }

      // Calculate avg daily sales
      for (const productId of Object.keys(productSales)) {
        productSales[productId].avgDailySales = productSales[productId].totalQty / 30;
      }

      // Build matrix data with real calculations
      const matrixData: MatrixItem[] = (inventoryData || [])
        .filter(inv => inv.products && (inv.system_qty || 0) > 0)
        .map(inv => {
          const product = inv.products!;
          const productId = inv.product_id;
          const sales = productSales[productId];
          const currentStock = inv.physical_qty || inv.system_qty || 0;
          const unitCost = product.unit_cost || 0;
          const unitPrice = product.unit_price || 0;
          const inventoryCost = currentStock * unitCost;

          // DOH = Current Inventory / Avg Daily Sales (days of stock remaining)
          // If no sales data, DOH cannot be calculated - return null equivalent (0)
          const avgDailySales = sales?.avgDailySales || 0;
          const doh = avgDailySales > 0
            ? Math.round(currentStock / avgDailySales)
            : 0; // No sales data = cannot calculate DOH

          // GMROI = Gross Margin / Avg Inventory Cost
          // Gross Margin = Revenue - COGS
          const grossMargin = sales ? (sales.totalRevenue - sales.totalCOGS) : 0;
          const gmroi = inventoryCost > 0
            ? Math.round((grossMargin / inventoryCost) * 100) / 100
            : 0;

          return {
            sku: product.sku,
            name: product.name,
            doh: Math.min(doh, 365), // Cap at 365 days
            gmroi: Math.max(0, gmroi), // Ensure non-negative
            category: product.category || 'General',
            value: currentStock * unitPrice
          };
        })
        .slice(0, 20);

      if (metricsError && !isSchemasCacheError(metricsError)) {
        console.debug('[InventoryRepository] inventory_quality_metrics not found for store:', storeId);
      }

      // Validate metrics if available
      let validatedMetrics: z.infer<typeof InventoryQualityMetricsDbSchema> | null = null;
      if (metrics) {
        try {
          validatedMetrics = InventoryQualityMetricsDbSchema.parse(metrics);
        } catch (e) {
          console.warn('[InventoryRepository] Failed to validate quality metrics:', e);
        }
      }

      return {
        ira: validatedMetrics ? Number(validatedMetrics.ira) : null,
        phantomRate: validatedMetrics ? Number(validatedMetrics.phantom_rate) : null,
        gmroi: validatedMetrics ? Number(validatedMetrics.gmroi) : null,
        doh: validatedMetrics ? Number(validatedMetrics.doh) : null,
        sellThrough: validatedMetrics ? Number(validatedMetrics.sell_through) : null,
        planogramCompliance: validatedMetrics ? Number(validatedMetrics.planogram_compliance) : null,
        shrinkageRate: validatedMetrics ? toNullableNumber(validatedMetrics.shrinkage_rate) : null,
        phantomItems,
        cashierRisks,
        matrixData
      };
    } catch (err) {
      console.error('[InventoryRepository] Error fetching inventory quality:', err);
      return this.getEmptyQualityMetrics();
    }
  },

  /**
   * Helper to return empty quality metrics structure.
   * Used when tenantId is missing or on errors.
   */
  getEmptyQualityMetrics(): InventoryQualityMetricsResult {
    return {
      ira: null,
      phantomRate: null,
      gmroi: null,
      doh: null,
      sellThrough: null,
      planogramCompliance: null,
      shrinkageRate: null,
      phantomItems: [],
      cashierRisks: [],
      matrixData: []
    };
  },

  /**
   * Get store financial summary including top performers and profit killers.
   * SECURITY: tenantId is required to prevent cross-tenant data leakage.
   * @param tenantId - Required tenant UUID for security filtering
   * @param storeId - The store UUID
   * @returns StoreFinancialSummary with validated data
   */
  async getStoreFinancials(tenantId: string, storeId: string): Promise<StoreFinancialSummary> {
    // SECURITY: Require tenantId to prevent data leakage
    if (!tenantId) {
      console.error('[InventoryRepository.getStoreFinancials] tenantId is required for security');
      return { totalLostProfit: 0, lastPeriodLostProfit: 0, topPerformers: [], profitKillers: [], categoryBreakdown: [] };
    }

    auditLogger.log({ tenantId, action: 'view', entityType: 'inventory', details: { query: 'financials', storeId } });

    try {
      // Get products with inventory for this store
      const { data: inventory, error: invError } = await supabase
        .from('inventory')
        .select(`
          product_id,
          system_qty,
          physical_qty,
          products (
            id,
            sku,
            name,
            category,
            unit_cost,
            unit_price
          )
        `)
        .eq('tenant_id', tenantId)  // SECURITY: Always filter by tenant
        .eq('store_id', storeId);

      if (invError) {
        console.error('[InventoryRepository] Failed to fetch inventory for financials:', invError);
        return { totalLostProfit: 0, lastPeriodLostProfit: 0, topPerformers: [], profitKillers: [], categoryBreakdown: [] };
      }

      if (!inventory || inventory.length === 0) {
        console.warn('[InventoryRepository] No inventory data found for store:', storeId);
        return { totalLostProfit: 0, lastPeriodLostProfit: 0, topPerformers: [], profitKillers: [], categoryBreakdown: [] };
      }

      // Get transactions for this store (last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      // SECURITY: Filter transactions by tenant_id
      const { data: transactions } = await supabase
        .from('transactions')
        .select('product_id, type, quantity, total_amount')
        .eq('tenant_id', tenantId)  // SECURITY: Always filter by tenant
        .eq('store_id', storeId)
        .gte('created_at', thirtyDaysAgo.toISOString());

      // Calculate product metrics
      const productMetrics: Record<string, {
        sku: string;
        name: string;
        category: string;
        cost: number;
        price: number;
        margin: number;
        marginPercent: number;
        velocity: number;
        accuracyScore: number;
        lostProfitOpportunity: number;
        totalSales: number;
        totalReturns: number;
        variance: number;
        inventoryValue: number;
      }> = {};

      // Validate and process inventory data
      for (const inv of (inventory || [])) {
        try {
          const validated = InventoryWithProductSchema.parse(inv);
          const product = validated.products;
          if (!product) continue;

          const systemQty = validated.system_qty || 0;
          const physicalQty = validated.physical_qty || 0;
          const variance = systemQty - physicalQty;
          const cost = Number(product.unit_cost) || 0;
          const price = Number(product.unit_price) || 0;
          const margin = price - cost;
          const marginPercent = price > 0 ? (margin / price) * 100 : 0;
          const lostProfit = variance > 0 ? variance * margin : 0;
          const accuracyScore = systemQty > 0
            ? Math.max(0, 100 - Math.abs(variance / systemQty * 100))
            : 100;
          // Use physical quantity for inventory value, fall back to system_qty if not counted
          const stockQty = physicalQty > 0 ? physicalQty : systemQty;
          const inventoryValue = stockQty * cost;

          productMetrics[product.id] = {
            sku: product.sku || '',
            name: product.name || 'Desconocido',
            category: product.category || 'Otra',
            cost,
            price,
            margin,
            marginPercent,
            velocity: 0,
            accuracyScore,
            lostProfitOpportunity: lostProfit,
            totalSales: 0,
            totalReturns: 0,
            variance,
            inventoryValue
          };
        } catch (e) {
          // Log validation errors for debugging
          console.warn('[InventoryRepository] Skipping invalid inventory record:', inv, 'Error:', e);
          continue;
        }
      }

      // Validate and process transactions
      for (const tx of (transactions || [])) {
        try {
          const validated = TransactionDbSchema.parse(tx);
          const pm = productMetrics[validated.product_id];
          if (!pm) continue;

          if (validated.type === 'SALE') {
            pm.totalSales += Number(validated.total_amount) || 0;
            pm.velocity += (validated.quantity || 0) / 30;
          } else if (validated.type === 'RETURN') {
            pm.totalReturns += Number(validated.total_amount) || 0;
          }
        } catch {
          // Skip invalid transaction records
          continue;
        }
      }

      // Calculate totals and sort
      const products = Object.values(productMetrics);
      const totalLostProfit = products.reduce((sum, p) => sum + p.lostProfitOpportunity, 0);

      // Top performers: high margin and high velocity
      const topPerformers: ProductFinancials[] = products
        .filter(p => p.velocity > 0)
        .sort((a, b) => (b.margin * b.velocity) - (a.margin * a.velocity))
        .slice(0, 5)
        .map(p => ({
          sku: p.sku,
          name: p.name,
          category: p.category,
          cost: p.cost,
          price: p.price,
          margin: p.margin,
          marginPercent: p.marginPercent,
          velocity: p.velocity,
          accuracyScore: p.accuracyScore,
          lostProfitOpportunity: p.lostProfitOpportunity,
          trend: (p.velocity > 2 ? 'up' : p.velocity < 0.5 ? 'down' : 'stable') as 'up' | 'down' | 'stable'
        }));

      // Profit killers: high lost profit opportunity
      const profitKillers: ProductFinancials[] = products
        .filter(p => p.lostProfitOpportunity > 0)
        .sort((a, b) => b.lostProfitOpportunity - a.lostProfitOpportunity)
        .slice(0, 5)
        .map(p => ({
          sku: p.sku,
          name: p.name,
          category: p.category,
          cost: p.cost,
          price: p.price,
          margin: p.margin,
          marginPercent: p.marginPercent,
          velocity: p.velocity,
          accuracyScore: p.accuracyScore,
          lostProfitOpportunity: p.lostProfitOpportunity,
          trend: (p.lostProfitOpportunity > 1000 ? 'down' : p.lostProfitOpportunity < 100 ? 'up' : 'stable') as 'up' | 'down' | 'stable'
        }));

      // Category breakdown - aggregate by category
      const categoryMap: Record<string, CategoryFinancials> = {};
      for (const p of products) {
        if (!categoryMap[p.category]) {
          categoryMap[p.category] = { name: p.category, totalLostProfit: 0, totalValue: 0, itemCount: 0 };
        }
        categoryMap[p.category].totalLostProfit += p.lostProfitOpportunity;
        categoryMap[p.category].totalValue += p.inventoryValue;
        categoryMap[p.category].itemCount++;
      }

      // Sort by total inventory value (more useful visualization)
      // Only require itemCount > 0; if totalValue is 0, it means no cost data
      const categoryBreakdown: CategoryFinancials[] = Object.values(categoryMap)
        .filter(cat => cat.itemCount > 0)
        .sort((a, b) => b.totalValue - a.totalValue)
        .slice(0, 6);

      // Validate final output
      // Note: lastPeriodLostProfit requires historical data - return 0 if not available
      const result = StoreFinancialSummarySchema.parse({
        totalLostProfit,
        lastPeriodLostProfit: 0, // Requires historical tracking to calculate
        topPerformers,
        profitKillers,
        categoryBreakdown
      });

      return result;
    } catch (err) {
      console.error('[InventoryRepository] Error fetching store financials:', err);
      return { totalLostProfit: 0, lastPeriodLostProfit: 0, topPerformers: [], profitKillers: [], categoryBreakdown: [] };
    }
  },

  /**
   * Get raw inventory records for a store.
   * SECURITY: tenantId is required to prevent cross-tenant data leakage.
   * @param tenantId - Required tenant UUID for security filtering
   * @param storeId - The store UUID
   * @returns Array of validated inventory records
   */
  async getInventoryByStore(tenantId: string, storeId: string): Promise<z.infer<typeof InventoryRecordSchema>[]> {
    // SECURITY: Require tenantId to prevent data leakage
    if (!tenantId) {
      console.error('[InventoryRepository.getInventoryByStore] tenantId is required for security');
      return [];
    }

    auditLogger.log({ tenantId, action: 'view', entityType: 'inventory', details: { query: 'by_store', storeId } });

    try {
      const { data, error } = await supabase
        .from('inventory')
        .select('*')
        .eq('tenant_id', tenantId)  // SECURITY: Always filter by tenant
        .eq('store_id', storeId);

      if (error) {
        console.error('[InventoryRepository] Failed to fetch inventory:', error);
        return [];
      }

      // Validate each record
      const validated = (data || []).map(record => {
        try {
          return InventoryRecordSchema.parse(record);
        } catch {
          return null;
        }
      }).filter((r): r is z.infer<typeof InventoryRecordSchema> => r !== null);

      return validated;
    } catch (err) {
      console.error('[InventoryRepository] Error fetching inventory by store:', err);
      return [];
    }
  },

  /**
   * Get shelf vs backroom split data for a store.
   * Uses qty_on_shelf and qty_in_backroom LP fields from inventory table.
   * SECURITY: tenantId required for tenant isolation.
   */
  async getShelfBackroomSplit(
    tenantId: string,
    storeId: string,
  ): Promise<{ sku: string; name: string; shelfQty: number; backroomQty: number }[]> {
    if (!tenantId) return [];

    try {
      const { data, error } = await supabase
        .from('inventory')
        .select(`
          qty_on_shelf,
          qty_in_backroom,
          products (
            sku,
            name
          )
        `)
        .eq('tenant_id', tenantId)
        .eq('store_id', storeId)
        .not('qty_on_shelf', 'is', null)
        .not('qty_in_backroom', 'is', null);

      if (error) {
        console.error('[InventoryRepository.getShelfBackroomSplit] Error:', error);
        return [];
      }

      return (data || [])
        .filter((row: any) => row.products)
        .map((row: any) => ({
          sku: row.products.sku || '',
          name: row.products.name || 'Desconocido',
          shelfQty: Number(row.qty_on_shelf) || 0,
          backroomQty: Number(row.qty_in_backroom) || 0,
        }))
        .sort((a, b) => (b.shelfQty + b.backroomQty) - (a.shelfQty + a.backroomQty))
        .slice(0, 20);
    } catch (err) {
      console.error('[InventoryRepository.getShelfBackroomSplit] Error:', err);
      return [];
    }
  },

  /**
   * Get items at risk of stock-out based on qty_available_to_sell.
   * SECURITY: tenantId required for tenant isolation.
   */
  async getStockOutRisk(
    tenantId: string,
    storeId: string,
    threshold: number = 0,
  ): Promise<{ sku: string; name: string; qtyAvailable: number; systemQty: number; category: string }[]> {
    if (!tenantId) return [];

    try {
      const { data, error } = await supabase
        .from('inventory')
        .select(`
          qty_available_to_sell,
          system_qty,
          products (
            sku,
            name,
            category
          )
        `)
        .eq('tenant_id', tenantId)
        .eq('store_id', storeId)
        .not('qty_available_to_sell', 'is', null)
        .lte('qty_available_to_sell', threshold);

      if (error) {
        console.error('[InventoryRepository.getStockOutRisk] Error:', error);
        return [];
      }

      return (data || [])
        .filter((row: any) => row.products)
        .map((row: any) => ({
          sku: row.products.sku || '',
          name: row.products.name || 'Desconocido',
          qtyAvailable: Number(row.qty_available_to_sell) || 0,
          systemQty: Number(row.system_qty) || 0,
          category: row.products.category || 'General',
        }))
        .sort((a, b) => a.qtyAvailable - b.qtyAvailable)
        .slice(0, 20);
    } catch (err) {
      console.error('[InventoryRepository.getStockOutRisk] Error:', err);
      return [];
    }
  },
};

// Export schemas for external use
export {
  InventoryRecordSchema,
  InventoryWithProductSchema,
  InventoryInsightSchema,
  StoreFinancialSummarySchema,
  ProductFinancialsSchema,
  CategoryFinancialsSchema,
};
